import { AxiosError } from 'axios';
import { AppContext } from 'context/AppContextProvider';
import { useLogout } from 'hooks/useLogout';
import { useContext, useEffect, useRef } from 'react';
import { api, getToken } from 'services/DefaultService';
import { IApiErrorData } from 'services/types';

type ErrorHandlers = {
	showSnackbar: (message: string, severity: 'warning' | 'error') => void;
	logout: (message?: string, severity?: 'warning' | 'info') => void;
};

const handleErrors = (
	error: AxiosError<IApiErrorData>,
	{ showSnackbar, logout }: ErrorHandlers,
) => {
	if (error.response && error.response.data) {
		const message =
			error.response.data.userMessage || 'Erro interno do servidor!';
		switch (error.response.status) {
			case 0: {
				showSnackbar(
					'Falha ao conectar-se ao servidor. Verifique sua conexão Wi-Fi!',
					'warning',
				);
				break;
			}
			case 401: {
				logout(message, 'warning');
				break;
			}
			default: {
				showSnackbar(message, 'error');
			}
		}
	}
};

export const useAxiosInterceptors = () => {
	const { showSnackbar } = useContext(AppContext);
	const logout = useLogout();
	const handlersRef = useRef<ErrorHandlers>({ showSnackbar, logout });
	handlersRef.current = { showSnackbar, logout };

	useEffect(() => {
		const requestInterceptorId = api.interceptors.request.use(
			(config) => {
				const token = getToken();
				if (token)
					config.headers.Authorization = `Bearer ${window.atob(token)}`;
				return config;
			},
			(error) => {
				return Promise.reject(error);
			},
		);

		const responseInterceptorId = api.interceptors.response.use(
			(config) => {
				return config;
			},
			(error: AxiosError<IApiErrorData>) => {
				if (!error?.config?._suppressError) {
					handleErrors(error, handlersRef.current);
				}
				return Promise.reject(error);
			},
		);

		return () => {
			api.interceptors.request.eject(requestInterceptorId);
			api.interceptors.response.eject(responseInterceptorId);
		};
	}, []);
};
